// src/components/AuthForm.js
import React, { useState } from 'react';
import { registerWithEmail, loginWithEmail, loginWithGoogle, resetPassword } from '../firebase/auth';

const titleOptions = [
  { value: '', label: 'Select a title (optional)' },
  { value: 'ABCS-chair', label: 'ABCS-chair' },
  { value: 'ABFCS-chair', label: 'ABFCS-chair' },
  { value: 'AACS-chair', label: 'AACS-chair' },
  { value: 'ABCS-committee', label: 'ABCS-committee member' },
  { value: 'ABFCS-committee', label: 'ABFCS-committee member' },
  { value: 'AACS-committee', label: 'AACS-committee member' },
  { value: 'fellowship-director', label: 'Fellowship Director' },
  { value: 'ABCS-staff', label: 'ABCS-staff' },
  { value: 'AACS-staff', label: 'AACS-staff' },
  { value: 'ABFCS-staff', label: 'ABFCS-staff' }
];

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);
    
    try {
      if (isResetPassword) {
        // Handle password reset
        await resetPassword(email);
        setSuccess('Password reset email sent. Check your inbox.');
      } else if (isLogin) {
        // Handle login
        await loginWithEmail(email, password);
        // No need to set success, user will be redirected by auth state change
      } else {
        // Handle registration
        const userData = {
          displayName,
          title,
          phoneNumber,
          avatarUrl
        };
        await registerWithEmail(email, password, userData);
        // No need to set success, user will be redirected by auth state change
      }
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    
    try {
      await loginWithGoogle();
      // No need to set success, user will be redirected by auth state change
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setIsLoading(false);
    }
  };
  
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already registered.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/weak-password':
        return 'Password is too weak.';
      case 'auth/operation-not-allowed':
        return 'Operation not allowed.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed before completing the sign in.';
      default:
        return errorCode || 'An error occurred. Please try again.';
    }
  };
  
  const toggleForm = () => {
    setIsLogin(!isLogin);
    setIsResetPassword(false);
    setError(null);
    setSuccess(null);
  };
  
  const toggleResetPassword = () => {
    setIsResetPassword(!isResetPassword);
    setError(null);
    setSuccess(null);
  };
  
  return (
    <div className="auth-form-container">
      <form onSubmit={handleSubmit} className="auth-form">
        <h2>{isResetPassword ? 'Reset Password' : isLogin ? 'Login' : 'Create Account'}</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {!isLogin && !isResetPassword && (
          <>
            <div className="form-group">
              <label htmlFor="displayName">Name*</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <select
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isLoading}
                className="form-select"
              >
                {titleOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="phoneNumber">Mobile Phone (for alerts)</label>
              <input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="(123) 456-7890"
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="avatarUrl">Profile Image URL</label>
              <input
                id="avatarUrl"
                type="url"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://example.com/your-photo.jpg"
                disabled={isLoading}
              />
            </div>
          </>
        )}
        
        <div className="form-group">
          <label htmlFor="email">Email*</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            disabled={isLoading}
          />
        </div>
        
        {!isResetPassword && (
          <div className="form-group">
            <label htmlFor="password">Password*</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isLogin ? "Enter your password" : "Create a password"}
              required
              disabled={isLoading}
              minLength={6}
            />
          </div>
        )}
        
        <button 
          type="submit" 
          className="auth-submit-button"
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : isResetPassword ? 'Send Reset Link' : isLogin ? 'Sign In' : 'Sign Up'}
        </button>
        
        {!isResetPassword && (
          <button 
            type="button" 
            className="google-sign-in-button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
              <g transform="matrix(1, 0, 0, 1, 0, 0)">
                <path d="M21.35,11.1H12v3.2h5.38c-0.5,2.36-2.22,4.1-5.38,4.1c-3.25,0-5.89-2.64-5.89-5.89 c0-3.25,2.64-5.89,5.89-5.89c1.56,0,2.96,0.62,4.03,1.61l2.3-2.3c-1.71-1.58-3.95-2.52-6.33-2.52c-5.18,0-9.36,4.18-9.36,9.36 c0,5.18,4.18,9.36,9.36,9.36c5.4,0,8.96-3.8,8.96-9.15C21.59,12.19,21.5,11.63,21.35,11.1z" fill="#4285F4"></path>
              </g>
            </svg>
            Sign in with Google
          </button>
        )}
        
        <div className="form-links">
          {isLogin && (
            <button 
              type="button" 
              className="form-link"
              onClick={toggleResetPassword}
              disabled={isLoading}
            >
              {isResetPassword ? 'Back to Login' : 'Forgot Password?'}
            </button>
          )}
          
          {!isResetPassword && (
            <button 
              type="button" 
              className="form-link"
              onClick={toggleForm}
              disabled={isLoading}
            >
              {isLogin ? 'Need an account? Sign Up' : 'Already have an account? Sign In'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AuthForm;